/**
 * Analytics event inspector — per docs/specs/analytics.md §4.7.
 * Modal showing the raw JSON payload + metadata for a single event.
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Modal } from '../components/modal';
import { Chip } from '../components/chip';
import type { AnalyticsEvent, Tone } from '../../lib/types';

const prefixTone: Record<string, Tone> = {
    auth: 'info',
    track: 'purple',
    share: 'cyan',
    chat: 'purple',
    billing: 'success',
    focus: 'warning',
    license: 'success',
    tag: 'cyan',
    bucket: 'info',
    comment: 'info',
    onboarding: 'success'
};

function toneForEvent(eventType: string): Tone {
    const prefix = eventType.split('.')[0];
    return prefixTone[prefix] ?? 'muted';
}

export function AnalyticsEventInspector({
    event,
    onClose
}: {
    event: AnalyticsEvent | null;
    onClose: () => void;
}) {
    const open = !!event;
    const [copied, setCopied] = useState(false);
    const json = event
        ? JSON.stringify(
              {
                  id: event.id,
                  event_type: event.event_type,
                  ts: event.ts,
                  user_email: event.user_email ?? null,
                  properties: event.properties ?? {}
              },
              null,
              2
          )
        : '';

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(json);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={event ? event.event_type : 'Event'}
            subtitle={event ? `Event ${event.id} · ${event.ts} UTC` : undefined}
            width={620}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleCopy} disabled={!open}>
                        {copied ? <Check size={13} /> : <Copy size={13} />}{' '}
                        {copied ? 'Copied' : 'Copy JSON'}
                    </button>
                    <button className="btn btn-primary" onClick={onClose}>
                        Close
                    </button>
                </>
            }
        >
            {event && (
                <>
                    <dl className="kv-list" style={{ marginBottom: 12 }}>
                        <dt>Event ID</dt>
                        <dd className="mono">{event.id}</dd>
                        <dt>Type</dt>
                        <dd>
                            <Chip tone={toneForEvent(event.event_type)}>{event.event_type}</Chip>
                        </dd>
                        <dt>Timestamp</dt>
                        <dd className="mono">{event.ts} UTC</dd>
                        <dt>User</dt>
                        <dd>{event.user_email ?? 'anonymous'}</dd>
                    </dl>
                    <div className="form-field">
                        <span className="form-label">Raw payload</span>
                        <pre
                            className="mono"
                            style={{
                                margin: 0,
                                padding: 12,
                                background: 'var(--bg-input)',
                                border: '1px solid var(--border)',
                                borderRadius: 6,
                                fontSize: 11,
                                lineHeight: 1.5,
                                color: 'var(--text-primary)',
                                overflowX: 'auto',
                                whiteSpace: 'pre'
                            }}
                        >
                            <code>{json}</code>
                        </pre>
                    </div>
                </>
            )}
        </Modal>
    );
}
